import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minimize2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { cn } from "@/shared/lib/cn";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ConversationList } from "../components/messaging/ConversationList";
import { ChatPanel } from "../components/messaging/ChatPanel";
import { useMessaging } from "../messaging/MessagingProvider";

export function MessagingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = t.safecheck.messaging;

  const {
    homeHref,
    subtitle,
    threads,
    selectedThreadId,
    selectedThread,
    threadMessages,
    draft,
    loading,
    error,
    usingApi,
    setDraft,
    selectThread,
    sendMessage,
    minimizeToFloating,
  } = useMessaging();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [mobileChatMode, setMobileChatMode] = useState(false);

  const filteredThreads = useMemo(() => {
    return threads.filter((thread) => {
      const haystack =
        `${thread.name} ${thread.subtitle} ${thread.lastMessage}`.toLowerCase();
      const bySearch = search ? haystack.includes(search.toLowerCase()) : true;
      const byFilter = filter === "unread" ? thread.unreadCount > 0 : true;
      return bySearch && byFilter;
    });
  }, [threads, search, filter]);

  const handleSelectThread = (threadId: string) => {
    selectThread(threadId);
    setMobileChatMode(true);
  };

  const handleMinimize = () => {
    if (!selectedThread) return;
    minimizeToFloating();
    navigate(homeHref);
  };

  const showListOnMobile = !mobileChatMode;
  const showChatOnMobile = mobileChatMode;

  return (
    <>
      <Breadcrumbs
        homeHref={homeHref}
        items={[
          { label: t.safecheck.nav.communication },
          { label: labels.pageTitle },
        ]}
      />

      <div className="theme-card overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">
            {labels.pageTitle}
          </h1>
          <p className="mt-1 text-sm text-muted">{subtitle}</p>
          {loading && <p className="mt-2 text-sm text-muted">Chargement…</p>}
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
          {usingApi && !loading && (
            <p className="mt-2 text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
          )}
        </div>

        <div className="h-[min(72vh,720px)] lg:grid lg:grid-cols-[340px_1fr]">
          <div
            className={cn(
              "h-full min-h-0",
              showListOnMobile ? "block" : "hidden lg:block",
            )}
          >
            <ConversationList
              threads={filteredThreads}
              selectedId={selectedThreadId}
              search={search}
              filter={filter}
              labels={{
                searchPlaceholder: labels.searchPlaceholder,
                filters: labels.filters,
                empty: labels.emptyList,
              }}
              onSearchChange={setSearch}
              onFilterChange={setFilter}
              onSelect={handleSelectThread}
            />
          </div>

          <div
            className={cn(
              "h-full min-h-0",
              showChatOnMobile ? "block" : "hidden lg:block",
            )}
          >
            <ChatPanel
              thread={selectedThread}
              messages={threadMessages}
              draft={draft}
              labels={labels.chat}
              showBackButton
              onBack={() => setMobileChatMode(false)}
              onDraftChange={setDraft}
              onSend={sendMessage}
              headerActions={
                selectedThread ? (
                  <button
                    type="button"
                    onClick={handleMinimize}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
                    aria-label={labels.floating.minimize}
                    title={labels.floating.minimize}
                  >
                    <Minimize2 size={16} />
                  </button>
                ) : null
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}
